import { Component, Element, Event, EventEmitter, h, Host, Prop } from "@stencil/core";

import { StzhPiCardNavigationClickEvent } from "../../../index";
import { hasSlot } from "../../../utils/utils";

/**
 * @slot heading - Heading text (alternative for heading property)
 * @slot lead - Lead text (alternative for lead property)
 * @slot image - Image (rendered within ratio 2:1, hence the different slot name to prevent naming collision)
 */
@Component({
  tag: "stzh-pi-content-navigation",
  styleUrl: "stzh-pi-content-navigation.scss",
  scoped: true,
})
export class StzhPiContentNavigation {
  /** Href of card */
  @Prop({ reflect: true }) href: string;

  /** target if the button is used as link (if href used)  */
  @Prop() target: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link elements.
   * Default value will be taken from `heading` prop/slot.
   */
  @Prop() analyticsId: string;

  /** Content navigation level */
  @Prop({ reflect: true }) navigationLevel: "1" | "2" | "3" | "4" = "1";

  /** Heading (alternative for heading slot) */
  @Prop() heading: string = "";

  /** Heading level */
  @Prop() headingLevel: "1" | "2" | "3" | "4" = "3";

  /** Lead text (alternative for lead slot) */
  @Prop() lead: string = "";

  @Element() element: HTMLStzhListElement;

  /** Click event (if href is used) */
  @Event() stzhClick: EventEmitter<StzhPiCardNavigationClickEvent>;

  private cardLinkElement: HTMLAnchorElement | HTMLButtonElement;
  private headingTextElement: HTMLDivElement;

  private handleCardClick = (originalEvent: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-pi-content-navigation",
      originalEvent,
      href: this.href,
    });
  };

  componentDidRender() {
    requestAnimationFrame(() => {
      const objectId = this.analyticsId || this.heading || this.headingTextElement?.innerText;
      if (objectId) {
        this.cardLinkElement?.setAttribute("s-object-id", objectId);
      }
    });
  }

  renderHeading(): DocumentFragment {
    const Heading = `h${this.headingLevel}`;
    return (
      <Heading class="stzh-pi-content-navigation__title" ref={el => (this.headingTextElement = el)}>
          {this.heading || <slot name="heading"></slot>}
      </Heading>
    );
  }

  render() {
    const hasLead = !!this.lead || hasSlot(this.element, "lead");
    const hasImageSlot = hasSlot(this.element, "image");
    const isNavigationLevel1or2 = this.navigationLevel === "1" || this.navigationLevel === "2";
    const hasImage = hasImageSlot && isNavigationLevel1or2;
    const shouldRenderTitleRatio = isNavigationLevel1or2 && !hasImage;

    const classes = {
      "stzh-pi-content-navigation": true,
      "stzh-pi-content-navigation--has-lead": hasLead,
      "stzh-pi-content-navigation--has-image": hasImage,
      "stzh-pi-content-navigation--is-navigation-level-1-or-2": isNavigationLevel1or2,
    };

    const CardLink = this.href ? "a" : "button";

    return (
      <Host has-lead={hasLead}>
        <article class={classes} aria-label={this.heading} role="listitem">
          <CardLink
            ref={el => (this.cardLinkElement = el as HTMLAnchorElement | HTMLButtonElement)}
            class="stzh-pi-content-navigation__title-link"
            href={this.href}
            target={this.target}
            onClick={this.handleCardClick}
          >
            <div class="stzh-pi-content-navigation__image">
              <stzh-ratio ratio="2:1">
                <slot name={"image"}></slot>
              </stzh-ratio>
            </div>
            {shouldRenderTitleRatio && (
              <div class="stzh-pi-content-navigation__title-ratio">
                {this.renderHeading()}
              </div>
            )}
            <div class="stzh-pi-content-navigation__wrapper">
              {!shouldRenderTitleRatio && this.renderHeading()}
              {hasLead &&
                <div class="stzh-pi-content-navigation__lead">{this.lead ? this.lead : <slot name="lead"></slot>}</div>
              }
            </div>

            <div class="stzh-pi-content-navigation__arrow">
              <stzh-icon class="stzh-pi-content-navigation__arrow-icon" name="arrow-right"></stzh-icon>
            </div>
          </CardLink>
        </article>
      </Host>
    );
  }
}
